import faker from 'faker';
import { Response } from 'miragejs';
import { StatusCodes as HTTP } from 'http-status-codes';
import { getSavedState, saveState, getLoggedUser } from '@/helpers/storageOperations';

export default server => {
    server.post('/v1/session', (schema, { requestBody }) => {
        const { password, email, otp_code: otpCode } = JSON.parse(requestBody);

        const user = schema.users.findBy({
            password,
            email
        });

        if (!user) {
            return new Response(HTTP.UNAUTHORIZED);
        }

        if (user.preferences?.otp_enabled) {
            if (!otpCode) {
                return new Response(
                    HTTP.UNAUTHORIZED,
                    {},
                    {
                        error_message: 'Invalid otp code'
                    }
                );
            }

            const OTP_CODE = '123456';

            if (otpCode !== OTP_CODE) {
                return new Response(HTTP.UNAUTHORIZED);
            }
        }

        const {
            attrs: { firstName, lastName, id, role, email: userEmail, preferences, businessProfile }
        } = user;

        return {
            account: {
                first_name: firstName,
                last_name: lastName,
                id,
                role,
                email: userEmail,
                preferences,
                businessProfile
            },
            csrf_token: 'tmp-csrf-token',
            access_token: 'tmp-access-token',
            refresh_token: 'tmp-refresh-token'
        };
    });

    server.delete('/v1/session', () => {
        return new Response(HTTP.NO_CONTENT);
    });

    server.get('/v1/accounts/new', (schema, { queryParams: { team_member_invite_token: tm } }) => {
        if (tm === 'correct') {
            return { email: 'joe@test.com' };
        }

        return new Response(HTTP.NOT_FOUND);
    });

    server.post('/v1/accounts/preferences/security/password_reset', (schema, { requestBody }) => {
        const { email } = JSON.parse(requestBody);

        if (email === 'test@aff.test' || email === 'affogataAuto+Guest@gmail.com') {
            return new Response(HTTP.NO_CONTENT);
        }

        return new Response(HTTP.UNAUTHORIZED);
    });

    server.get('/v1/accounts/preferences/security/password_reset', (schema, { queryParams: { token } }) => {
        if (token === 'correct') {
            return { name: 'Joe' };
        }

        return new Response(HTTP.UNAUTHORIZED);
    });

    server.put('/v1/accounts/preferences/security/password_reset/', (schema, { requestBody }) => {
        const { token } = JSON.parse(requestBody);

        if (token === 'correct') {
            return new Response(HTTP.NO_CONTENT);
        }

        return new Response(HTTP.FORBIDDEN);
    });

    server.post('/v1/accounts', (schema, { requestBody }) => {
        const {
            account: { email }
        } = JSON.parse(requestBody);

        const account = schema.users.findBy({ email });

        if (account) {
            return new Response(
                HTTP.UNPROCESSABLE_ENTITY,
                {},
                {
                    errors: {
                        email: 'Email is already taken!'
                    }
                }
            );
        }

        return new Response(HTTP.CREATED);
    });

    server.post('/v1/admin/accounts/takeover', (schema, { requestBody }) => {
        const { account_id: id } = JSON.parse(requestBody);

        const user = schema.users.find(id);

        const {
            attrs: { firstName, lastName, role, email }
        } = user;

        const { id: adminId } = getSavedState('auth.currentUser');
        saveState('mock.adminId', adminId);

        return {
            account: {
                first_name: firstName,
                last_name: lastName,
                id,
                role,
                email
            },
            csrf_token: 'tmp-csrf-token',
            access_token: 'tmp-access-token',
            refresh_token: 'tmp-refresh-token'
        };
    });

    server.delete('/v1/admin/accounts/takeover', schema => {
        const id = getSavedState('mock.adminId');
        const user = schema.users.find(id);

        const {
            attrs: { firstName, lastName, role, email }
        } = user;

        return {
            account: {
                first_name: firstName,
                last_name: lastName,
                id,
                role,
                email
            },
            csrf_token: 'tmp-csrf-token',
            access_token: 'tmp-access-token',
            refresh_token: 'tmp-refresh-token'
        };
    });

    server.put('/v1/accounts/preferences/general', (schema, { requestBody }) => {
        const { id } = getLoggedUser();

        const user = schema.users.find(id);

        if (!user) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { url_warning, review_on_reply, direct_email_to_network } = JSON.parse(requestBody);

        user.update({
            preferences: {
                ...user.attrs.preferences,
                general: {
                    url_warning,
                    review_on_reply,
                    direct_email_to_network
                }
            },
        });

        return user.attrs;
    });

    server.post('/v1/accounts/preferences/security/two_factor_authentication/setup', () => {
        return {
            otp_backup_codes: [
                '000000',
                '000001',
                '000002',
                '000003',
                '000004',
                '000005',
                '000006',
                '000007',
                '000008',
                '000009'
            ],
            otp_qr_code_svg:
                '<img src="https://www.eslkidsgames.com/wp-content/uploads/2020/10/QR-Code-Roleplay-Card-Example.png?ezimgfmt=rs:250x250/rscb1/ng:webp/ngcb1" width="147px" height="147px" />',
            otp_secret_key: 'CK3UPDZFZ64ZIMVXQKGDGKP4KSHHSLG2'
        };
    });

    server.post('/v1/accounts/preferences/security/two_factor_authentication/activate', (schema, { requestBody }) => {
        const { otp_code: code } = JSON.parse(requestBody);

        if (code !== '123456') {
            return new Response(HTTP.UNPROCESSABLE_ENTITY);
        }

        const { id } = getLoggedUser();

        const user = schema.users.find(id);

        if (!user) {
            return new Response(HTTP.NOT_FOUND);
        }

        user.update({
            preferences: {
                ...user.attrs.preferences,
                otp_enabled: true
            }
        });

        return new Response(HTTP.NO_CONTENT);
    });

    server.post('/v1/accounts/preferences/security/two_factor_authentication/regenerate_backup_codes', () => {
        const codes = [];

        for (let i = 0; i < 10; i++) {
            codes.push(`000${faker.datatype.number({ min: 100, max: 999 })}`);
        }

        return {
            otp_backup_codes: codes,
            otp_qr_code_svg:
                '<img src="https://www.eslkidsgames.com/wp-content/uploads/2020/10/QR-Code-Roleplay-Card-Example.png?ezimgfmt=rs:250x250/rscb1/ng:webp/ngcb1" width="147px" height="147px" />',
            otp_secret_key: 'CK3UPDZFZ64ZIMVXQKGDGKP4KSHHSLG2'
        };
    });
};
